import { useEffect, useRef } from "react";
import { StrapiMedia } from "@/components/shared/StrapiMedia";
import { MASK_SVG_PATH } from "@/components/screens/SplashScreen/components/IntroSequence/components/WelcomeSequence/components/RoomMask";
import { ROOM_IMAGE_SIZE } from "@/components/screens/SplashScreen/components/IntroSequence/components/WelcomeSequence/components/RoomOption";
import {Room} from "@/types/api";

type RoomTransitionOverlayProps = {
    room: Room;
    startRect: DOMRect;
    containerRect: DOMRect;
    durationS?: number;
    onComplete: () => void;
};

/**
 * Unified room transition overlay.
 *
 * Combines the image zoom and the inverted mask into a single component
 * driven by one animation loop, eliminating sync issues.
 *
 * All coordinates are relative to the container (the max-w-md wrapper),
 * and the whole overlay is clipped to the container bounds.
 *
 * Must be rendered via createPortal(_, document.body).
 */
export function RoomTransitionOverlay({
    room,
    startRect,
    containerRect,
    durationS = 0.9,
    onComplete,
}: RoomTransitionOverlayProps) {
    const gRef = useRef<SVGGElement>(null);
    const completeCalled = useRef(false);
    const imgRef = useRef<HTMLDivElement>(null);
    // All coordinates relative to container
    const cw = containerRect.width;
    const ch = containerRect.height;

    const relStartLeft = startRect.left - containerRect.left;
    const relStartTop = startRect.top - containerRect.top;

    const pathCx = ROOM_IMAGE_SIZE / 2;

    // Mask: initial transform in container-relative coords
    const initialCx = relStartLeft + startRect.width / 2;
    const initialCy = relStartTop + startRect.height / 2;
    const initialScale = startRect.width / ROOM_IMAGE_SIZE;
    const initialTx = initialCx - pathCx * initialScale;
    const initialTy = initialCy - pathCx * initialScale;

    useEffect(() => {
        const g = gRef.current;
        const el = imgRef.current;
        if (!g || !el) return;

        const ease = (t: number) => {
            if (t < 0.5) return 4 * t * t * t;
            return 1 - Math.pow(-2 * t + 2, 3) / 2;
        };

        const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

        const setMaskTransform = (cx: number, cy: number, s: number) => {
            const tx = cx - pathCx * s;
            const ty = cy - pathCx * s;
            g.setAttribute("transform", `translate(${tx}, ${ty}) scale(${s})`);
        };

        const durationMs = durationS * 1000;
        let startTime: number | null = null;
        let rafId: number;

        const animate = (timestamp: number) => {
            if (!startTime) startTime = timestamp;
            const elapsed = timestamp - startTime;
            const rawT = Math.min(elapsed / durationMs, 1);
            const t = ease(rawT);

            // Image — finishes early (e.g. first 60% of duration)
            const imgT = Math.min(t / 0.6, 1);
            const curLeft = lerp(relStartLeft, 0, imgT);
            const curTop = lerp(relStartTop, 0, imgT);
            const curW = lerp(startRect.width, cw, imgT);
            const curH = lerp(startRect.height, ch, imgT);

            el.style.left = `${curLeft}px`;
            el.style.top = `${curTop}px`;
            el.style.width = `${curW}px`;
            el.style.height = `${curH}px`;
            el.style.borderRadius = `${lerp(8, 0, imgT)}px`;

            // Mask — runs the full duration, fit to width so it never exceeds image horizontally
            const cx = curLeft + curW / 2;
            const cy = curTop + curH / 2;

            // During image phase: mask tracks image width tightly
            // After image phase: mask keeps growing toward full container diagonal
            const maskFitWidth = curW / ROOM_IMAGE_SIZE;
            const maskFinal = (Math.hypot(cw, ch) * 1.3) / ROOM_IMAGE_SIZE;
            const maskGrowT = Math.max((t - 0.6) / 0.4, 0); // 0 during image phase, 0→1 after
            const s = lerp(maskFitWidth, maskFinal, maskGrowT);

            setMaskTransform(cx, cy, s);

            if (rawT < 1) {
                rafId = requestAnimationFrame(animate);
            } else if (!completeCalled.current) {
                completeCalled.current = true;
                onComplete();
            }
        };

        rafId = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(rafId);
    }, [startRect, containerRect, durationS, onComplete]);

    return (
        <div
            style={{
                position: "absolute",
                left: 0,
                top: 0,
                width: cw,
                height: ch,
                overflow: "hidden",
                zIndex: 9999,
                pointerEvents: "none",
            }}
        >
            {/* Image zoom */}
            <div
                ref={imgRef}
                style={{
                    position: "absolute",
                    left: relStartLeft,
                    top: relStartTop,
                    width: startRect.width,
                    height: startRect.height,
                    borderRadius: 8,
                    overflow: "hidden",
                    pointerEvents: "none",
                }}
            >
                <StrapiMedia size="lg" image={room.image} alt={room.name} className="h-full w-full object-cover" />
            </div>

            {/* Mask overlay */}
            <svg
                width={cw}
                height={ch}
                viewBox={`0 0 ${cw} ${ch}`}
                xmlns="http://www.w3.org/2000/svg"
                style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1,
                }}
            >
                <defs>
                    <linearGradient id="mask-bg-gradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ECE7EC" />
                        <stop offset="100%" stopColor="#FFF" />
                    </linearGradient>
                    <mask id="inverse-cutout-mask" maskUnits="userSpaceOnUse">
                        <rect width={cw} height={ch} fill="white" />
                        <g ref={gRef} transform={`translate(${initialTx}, ${initialTy}) scale(${initialScale})`}>
                            <path d={MASK_SVG_PATH} fill="black" />
                        </g>
                    </mask>
                </defs>

                <rect width={cw} height={ch} fill="url(#mask-bg-gradient)" mask="url(#inverse-cutout-mask)" />
            </svg>
        </div>
    );
}
